import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import QuietMarketingLayout, {
  QuietPrimaryButton,
  QuietSecondaryButton,
} from "../components/QuietMarketingLayout";
import { useAuth } from "../lib/auth";
import { HOST_DEMO_PATH } from "../lib/demoChooser";
import { hostCtaTarget } from "../lib/hostProfile";

const linkFocus =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2 rounded-sm";

const bodyCopyClass = "text-[15px] font-normal text-ink/65 leading-relaxed text-pretty";

function BenefitItem({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-[15px] font-normal text-ink/70 leading-relaxed">
      <span className="text-accent shrink-0 mt-1.5 w-1 h-1 rounded-full bg-accent" aria-hidden />
      <span>{children}</span>
    </li>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 className="font-ui font-semibold text-[16px] sm:text-[17px] text-ink leading-snug tracking-normal">
      {children}
    </h2>
  );
}

function SectionDivider({ children }) {
  return <div className="mt-8 pt-8 border-t border-black/[0.06]">{children}</div>;
}

export default function ForOrganizersPage() {
  const { account } = useAuth();
  const hostCta = hostCtaTarget(account);

  useEffect(() => {
    document.title = "For hosts — rsvp.dance";
    return () => {
      document.title = "Find your next social.";
    };
  }, []);

  return (
    <QuietMarketingLayout
      title="Turn first-time dancers into regulars."
      description="Simple event RSVPs built to keep new dancers coming back."
      backTestId="for-organizers-back-link"
      brandTestId="for-organizers-brand-link"
      heroExtra={
        <div className="mt-8">
          <QuietPrimaryButton
            as={Link}
            to={hostCta.path}
            data-testid="for-organizers-hero-host-cta"
          >
            Host an Event
          </QuietPrimaryButton>
        </div>
      }
    >
      <section className="rounded-2xl border border-black/[0.04] bg-white p-5 sm:p-6 mb-12">
        <div>
          <SectionHeading>Built for host efficiency and dancer retention</SectionHeading>
          <ul className="mt-3 space-y-3">
            <BenefitItem>Send updates without WhatsApp group clutter.</BenefitItem>
            <BenefitItem>Give dancers one-tap RSVPs via a single link.</BenefitItem>
            <BenefitItem>Invite guests to post-class hangouts automatically.</BenefitItem>
            <BenefitItem>Notify dancers when their favorite partners attend.</BenefitItem>
          </ul>
        </div>

        <SectionDivider>
          <SectionHeading>Your data stays yours.</SectionHeading>
          <ul className="mt-3 space-y-3">
            <BenefitItem>
              <strong className="font-semibold text-ink">100% Private:</strong> We never share guest
              lists or promote competitor events to your attendees.
            </BenefitItem>
            <BenefitItem>
              <strong className="font-semibold text-ink">Full Ownership:</strong> Export your guest
              data from your dashboard anytime.
            </BenefitItem>
            <BenefitItem>
              <strong className="font-semibold text-ink">Open Ecosystem:</strong> Help dancers
              discover events without locking them into a closed platform.
            </BenefitItem>
          </ul>
        </SectionDivider>

        <SectionDivider>
          <SectionHeading>Pricing</SectionHeading>
          <p className={`mt-3 ${bodyCopyClass}`}>
            <strong className="font-semibold text-ink">Events are 100% free.</strong>
          </p>
          <p className={`mt-1.5 ${bodyCopyClass}`}>No setup fees, no ticket cuts, ever.</p>
          <p className={`mt-4 ${bodyCopyClass}`}>
            <strong className="font-semibold text-ink">Dance School Management (Coming Soon)</strong>
          </p>
          <p className={`mt-1.5 ${bodyCopyClass}`}>
            Optional tools for managing classes, students, and memberships will start at $10 to $15
            per month for active schools.
          </p>
          <p className={`mt-4 ${bodyCopyClass}`}>
            Have questions? Contact{" "}
            <a
              href="https://x.com/navalsaini"
              target="_blank"
              rel="noopener noreferrer"
              className={`text-ink underline underline-offset-2 ${linkFocus}`}
            >
              @navalsaini
            </a>
            .
          </p>
        </SectionDivider>
      </section>

      <section className="pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <p className="font-display font-semibold text-[18px] sm:text-[20px] mb-5 text-balance leading-snug text-ink">
          Ready to grow your dance community?
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <QuietPrimaryButton
            as={Link}
            to={hostCta.path}
            className="w-full sm:w-auto"
            data-testid="for-organizers-host-cta"
          >
            Host an Event
          </QuietPrimaryButton>
          <QuietSecondaryButton
            as={Link}
            to={HOST_DEMO_PATH}
            className="w-full sm:w-auto"
            data-testid="for-organizers-host-demo-cta"
          >
            Try the Demo
          </QuietSecondaryButton>
        </div>
      </section>
    </QuietMarketingLayout>
  );
}
